/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useState } from 'react';
import { connect } from 'react-redux';
import {
    object, func, shape, string,
} from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import {
    Card, CardContent, CardHeader, Grid, IconButton, Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import Button from '@material-ui/core/Button/Button';
import { useQuery } from '@apollo/client';
import mbpLogger from '@module-logger/mbp';
import EventDispatcher from '../../../state/reducers/EventDispatcher';
import SubscriptionDropDown from '../Partials/SubscriptionDropDown';
import { getBrand } from '../../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import findSkuById from '../../../../../gql/queries/findSkuById';
import ErrorDialog from '../Dialogs/ErrorDialog';
import { isEmpty } from '../../../utils/object';

const styles = (theme) => ({
    cardHeaderWrap: {
        borderBottom: `1px solid ${theme.palette.grey5}`,
    },
    cardHeaderRoot: {
        padding: '12px',
    },
    cardHeaderTitle: {
        fontSize: '15px',
        fontWeight: '600',
    },
    scrollStyle: {
        overflow: 'inherit',
        height: '100%',
        position: 'relative',
    },
    paddingContainer: {
        paddingBottom: '23px 19.5px !important',
    },
    secondaryButtonStyle: {
        backgroundColor: 'transparent',
        color: theme.palette.black,
        fontSize: '14px',
        fontWeight: 'bold',
        padding: '8px 16px',
        borderRadius: '30px',
        border: `1px solid ${theme.palette.black}`,
        width: '48%',
        boxShadow: '2px 2px 4px rgba(47, 47, 47, 0.15)',
    },
    primaryButtonStyle: {
        color: theme.palette.white,
        backgroundColor: theme.palette.primaryButton,
        fontSize: '14px',
        fontWeight: 'bold',
        padding: '8px 16px',
        borderRadius: '30px',
        border: `1px solid ${theme.palette.primaryButton}`,
        boxShadow: '2px 2px 4px rgba(47, 47, 47, 0.15)',
        width: '48%',
    },
    btnContainer: {
        marginBottom: '22px',
        padding: '0 20px',
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
    },
    dropDownContainer: {
        minHeight: '450px',
        maxHeight: '540px',
    },
});

const SubscriptionFrequencyForm = ({
    openModal, classes, closeModal, brand, subscription, updateSubFrequency, updateSubscriptionList, enableEditButton,
}) => {
    const [selectedFrequencyValue, setFrequencyValue] = useState({});
    const [selectedDurationValue, setDurationValue] = useState({});

    const { productId } = subscription;
    const productSku = `${subscription.brand}-${productId}`;

    const query = findSkuById(brand.domain, productSku, 'subscriptionsList { interval, duration, type } ');
    const { error, data, loading } = useQuery(query);

    if (loading || !data) {
        return null;
    }

    const errorMsg = 'We are sorry, there seems to be a technical issue, please try again';
    if (error) {
        mbpLogger.logError({
            appName: process.env.npm_package_name,
            jsError: error,
            message: `Failed to fetch CSM subscriptionsList data: content type findSkuById - query - ${query}`,
        });
        return (
            <ErrorDialog
                classes={classes}
                closeModal={closeModal}
                openModal={openModal}
                errorMessage={errorMsg}
            />
        );
    }

    let subscriptionsList = {};
    if (data) {
        subscriptionsList = data?.findSkuById?.subscriptionsList;
    }

    if (!isEmpty(subscriptionsList) && Object.keys(subscriptionsList).length < 1) {
        mbpLogger.logError({
            appName: process.env.npm_package_name,
            jsError: error,
            message: `CSM SubscriptionsList data is empty, no frequency options found ${subscriptionsList}`,
        });
        return (
            <ErrorDialog
                classes={classes}
                closeModal={closeModal}
                openModal={openModal}
                errorMessage={errorMsg}
            />
        );
    }

    const submitFrequencyChange = (e) => {
        e.preventDefault();
        const { ruleId } = subscription;
        const frequencyData = {
            ruleId,
            frequency: selectedFrequencyValue,
            duration: selectedDurationValue,
        };
        updateSubFrequency(frequencyData);
        updateSubscriptionList({ ...subscription, interval: selectedFrequencyValue });
        closeModal();
    };

    return (
        <Card className={classes.scrollStyle} elevation={0}>
            <Grid container className={classes.cardHeaderWrap}>
                <Grid item xs={10}>
                    <Typography component="span">
                        <CardHeader
                            data-test="checkout-recipient-header"
                            title="FREQUENCY DURATION"
                            classes={{
                                root: classes.cardHeaderRoot,
                                title: classes.cardHeaderTitle,
                            }}
                        />
                    </Typography>
                </Grid>
                <Grid container item xs={2} justify="flex-end">
                    <IconButton
                        aria-label="delete"
                        align="right"
                        data-test="checkout-recipient-form-close"
                        data-testid="checkout-recipient-form-close"
                        id="recipientFormCloseLink"
                        onClick={closeModal}
                        onKeyPress={closeModal}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Grid>
            </Grid>
            <CardContent className={`${classes.paddingContainer} ${classes.formContainer}`}>
                <div className={classes.modalContainer}>
                    <div className={classes.dropDownContainer}>
                        <SubscriptionDropDown
                            fieldLabel="Frequency"
                            enableEditButton={enableEditButton}
                            subscription={subscription}
                            subscriptionsList={subscriptionsList}
                            setFrequencyValue={setFrequencyValue}
                            setDurationValue={setDurationValue}
                        />
                    </div>
                    <div className={classes.btnContainer}>
                        <Button onClick={closeModal} className={classes.secondaryButtonStyle}>Cancel</Button>
                        <Button onClick={(e) => submitFrequencyChange(e)} className={classes.primaryButtonStyle}>Save</Button>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};

SubscriptionFrequencyForm.defaultProps = {
    brand: {},
    subscription: {},
    openModal: () => {},
    closeModal: () => {},
};
SubscriptionFrequencyForm.propTypes = {
    classes: object.isRequired,
    brand: shape({
        domain: string,
    }),
    subscription: shape({
        productId: string,
    }),
    closeModal: func,
    openModal: func,
    updateSubFrequency: func.isRequired,
    updateSubscriptionList: func.isRequired,
    enableEditButton: func.isRequired,
};

const mapDispatchToProps = (dispatch) => ({
    updateSubFrequency: (frequencyData) => dispatch(EventDispatcher.updateSubscriptionFrequency(frequencyData)),
});

const mapStateToProps = (state) => ({
    brand: getBrand(state),
});
export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(SubscriptionFrequencyForm));
